import { ref, computed, readonly, onMounted, onBeforeUnmount } from 'vue';
import { createGlobalState, useStorage } from '@vueuse/core';

const useGlobalState = createGlobalState(() => {
  /**
   * @type {Record<string | symbol, Record<symbol | string, boolean>>}
   */
  const state = {};
  const storage = useStorage('global-mutation-state', state);
  return storage;
});

export const useMutation = (fn, options) => {
  const uniqueMutationSymbol = options?.key ?? 'mutation_unique_key';
  const uniqueKey = options?.key ? `${options.key}_key` : 'unique_key';
  const state = useGlobalState();
  state.value[uniqueMutationSymbol] = { ...(state.value[uniqueMutationSymbol] || {}), [uniqueKey]: false };

  const data = ref(null);
  const isLoading = ref(false);
  const error = ref(null);

  const reset = () => {
    data.value = null;
    isLoading.value = false;
    state.value[uniqueMutationSymbol][uniqueKey] = false;
    error.value = null;
  };

  const resetError = () => {
    error.value = null;
  };

  const mutate = async (...args) => {
    await options?.onBefore?.();
    window.removeEventListener('pageshow', reset);
    isLoading.value = true;
    state.value[uniqueMutationSymbol][uniqueKey] = true;
    error.value = null;
    options?.onMutate?.();
    try {
      const result = await fn(...args);
      options?.onSuccess?.(result);
      data.value = result;
      return result;
    } catch (e) {
      error.value = e;
      options?.onError?.(e);
      throw e;
    } finally {
      isLoading.value = false;
      state.value[uniqueMutationSymbol][uniqueKey] = false;
      options?.onAfter?.(error.value, data.value);
    }
  };

  if (options?.resetOnPageChange) {
    onMounted(() => {
      window.addEventListener('pageshow', reset);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('pageshow', reset);
    });
  }

  return {
    data,
    mutate,
    isLoading: readonly(isLoading),
    isAnyLoading: computed(() => Reflect.ownKeys(state.value[uniqueMutationSymbol]).some((key) => state.value[uniqueMutationSymbol][key])),
    error,
    reset,
    resetError,
  };
};

export default useMutation;
